/**
 * Shapes of the shared grid preferences, ported from TMS `src/lib/gridHeaders.ts`.
 *
 * TMS interleaved seven `upsert` calls with these types. The writes moved to
 * `settings.ts`, which is per organization; what stays here is pure — the
 * shapes and the reducers that produce the next value. That split is why these
 * can be unit-tested and reused from the Worker.
 *
 * Every map is keyed by `gridKey` first, so one organization's grids never read
 * each other's layout. The column key inside is the `field`, which survives
 * column reordering, falling back to `#index` for purely calculated columns
 * that have none.
 */

import { scopeIncludes, type Device, type DeviceScope } from '@/shared/device/device';

/** `gridKey` → column key → the name the user typed over the default. */
export type GridHeaders = Record<string, Record<string, string>>;

/**
 * `gridKey` → stable column keys, in display order.
 *
 * Numeric indices from earlier releases remain readable so existing layouts
 * survive the rollout, but new writes use stable keys.
 */
export type GridColumnOrder = Record<string, string[] | number[]>;

/** `gridKey` → row ids, in the order the user dragged them into. */
export type GridRowOrder = Record<string, string[]>;

export interface GridSortRef {
  field: string | null;
  index: number;
  dir: 1 | -1;
}

export type GridSort = Record<string, GridSortRef>;

export interface GridGroupRefItem {
  field: string | null;
  header: string;
  index: number;
}

export type GridGroupRef = GridGroupRefItem | GridGroupRefItem[] | null;

export type GridGrouping = Record<string, GridGroupRef>;

/** `gridKey` → column key → width in px. */
export type GridWidths = Record<string, Record<string, number>>;

/** `gridKey` → hidden column keys. */
export type GridHiddenColumns = Record<string, string[]>;

/**
 * The last column frozen at the left edge: one key for every device, or one per
 * device when desktop and mobile freeze differently (either may be missing).
 */
export type GridFrozenEntry = string | { readonly desktop?: string; readonly mobile?: string };

/** `gridKey` → the columns frozen at the left edge. */
export type GridFrozenColumns = Record<string, GridFrozenEntry>;

/** `columnsKey` → column key → the only device that column is shown on. Missing means every device. */
export type GridColumnDevices = Record<string, Record<string, Device>>;

/** The column frozen through on `device`, if any. */
export function frozenKeyFor(entry: GridFrozenEntry | undefined, device: Device): string | null {
  if (!entry) return null;
  if (typeof entry === 'string') return entry;
  return entry[device] ?? null;
}

/** On which devices the grid freezes through `key`, or null when on none. */
export function frozenScopeOf(entry: GridFrozenEntry | undefined, key: string): DeviceScope | null {
  const desktop = frozenKeyFor(entry, 'desktop') === key;
  const mobile = frozenKeyFor(entry, 'mobile') === key;
  if (desktop && mobile) return 'all';
  if (desktop) return 'desktop';
  if (mobile) return 'mobile';
  return null;
}

/**
 * Freeze through `key` on the devices of `scope` (null `key`: unfreeze them),
 * leaving the other device as it was. Returns undefined when nothing stays
 * frozen, and the plain string form when both devices agree.
 */
export function withFrozenOn(entry: GridFrozenEntry | undefined, key: string | null, scope: DeviceScope): GridFrozenEntry | undefined {
  const next: { desktop?: string; mobile?: string } = {};
  for (const device of ['desktop', 'mobile'] as const) {
    const value = scopeIncludes(scope, device) ? key : frozenKeyFor(entry, device);
    if (value) next[device] = value;
  }
  if (next.desktop && next.desktop === next.mobile) return next.desktop;
  if (!next.desktop && !next.mobile) return undefined;
  return next;
}

/** `gridKey` → custom singular item name (e.g. 'Hotel', 'Expense', 'Task'). */
export type GridItemNames = Record<string, string>;

/**
 * The stable key for a column. `field` survives reordering; a calculated column
 * without one falls back to its index, which does not — that is why
 * `docs/Grid/GRID.md` tells you to give editable rows an id.
 */
export const columnKey = (field: string | undefined, index: number): string =>
  field ?? `#${index}`;

/**
 * Resolve a saved layout against the columns that exist right now.
 *
 * A column the saved order has never seen is placed next to the neighbour it sits
 * beside in the column list itself — its hidden ordinal — rather than at the end.
 * Appending was wrong the moment "insert column left/right" existed: the new column
 * would be written into the middle of the sheet and then still render last.
 */
export function resolveColumnOrder(
  saved: readonly string[] | readonly number[] | undefined,
  columnKeys: readonly string[],
): number[] {
  const byKey = new Map(columnKeys.map((key, index) => [key, index]));
  const present = new Set<number>();

  for (const entry of saved ?? []) {
    const index = typeof entry === 'string'
      ? byKey.get(entry)
      : Number.isInteger(entry) && entry >= 0 && entry < columnKeys.length
        ? entry
        : undefined;
    if (index !== undefined) present.add(index);
  }

  const result = [...present];
  const placed = new Set(present);
  // Ascending, so a run of new neighbours keeps its own relative order: each one
  // lands directly after the previous column that already has a slot.
  for (let index = 0; index < columnKeys.length; index += 1) {
    if (placed.has(index)) continue;
    let at = 0;
    for (let previous = index - 1; previous >= 0; previous -= 1) {
      if (!placed.has(previous)) continue;
      at = result.indexOf(previous) + 1;
      break;
    }
    result.splice(at, 0, index);
    placed.add(index);
  }
  return result;
}

/**
 * Which column a newly inserted one must follow, for an "insert left/right" issued
 * against `activeKey`.
 *
 * Left and right are DISPLAY concepts, so they are resolved against `displayOrder`
 * — not against the position of the column in any array. The answer is a stable key
 * (or `null` for "before everything") because the grid's own column array is derived
 * and its indices do not line up with the caller's stored column list. Answering with
 * an index is what put an inserted column one slot too far left.
 */
export function columnInsertAnchor(
  displayOrder: readonly number[],
  columnKeys: readonly string[],
  activeKey: string,
  where: 'left' | 'right',
): string | null {
  if (where === 'right') return activeKey;
  const activeIndex = columnKeys.indexOf(activeKey);
  if (activeIndex === -1) return null;
  const position = displayOrder.indexOf(activeIndex);
  if (position <= 0) return null;
  return columnKeys[displayOrder[position - 1]!] ?? null;
}

/**
 * Rank each row key against a saved row order.
 *
 * A key the saved order has never seen — a row just inserted above or below another —
 * takes the rank of the nearest preceding key that IS in the saved order, plus a hidden
 * offset so a run of new rows keeps its own order. Sending unknown keys to the end
 * instead would drop every inserted row at the bottom of the sheet.
 *
 * Returns the keys in display order. An empty saved order means "use data order".
 */
export function resolveRowOrder(
  keys: readonly string[],
  saved: readonly string[] | null | undefined,
): string[] {
  if (!saved || saved.length === 0) return [...keys];
  const pos = new Map(saved.map((key, index) => [key, index]));
  let lastSavedIdx = -1;
  for (let i = keys.length - 1; i >= 0; i--) {
    if (pos.has(keys[i]!)) {
      lastSavedIdx = i;
      break;
    }
  }

  let rank = -1;
  let offset = 0;
  return keys
    .map((key, i) => {
      const at = pos.get(key);
      if (at !== undefined) {
        rank = at;
        offset = 0;
      } else if (lastSavedIdx !== -1 && i > lastSavedIdx) {
        rank = saved.length;
        offset += 1;
      } else {
        offset += 1;
      }
      return { key, i, rank, offset };
    })
    .sort((a, b) => (a.rank !== b.rank ? a.rank - b.rank : a.offset !== b.offset ? a.offset - b.offset : a.i - b.i))
    .map(({ key }) => key);
}

/**
 * A copy of `all` with one header override applied. Clearing the name, or
 * typing the original back, removes the override — and the grid's entry with it
 * when it was the last one — so the column falls back to its own `headerName`
 * rather than storing a redundant copy of it forever.
 */
export function withHeader(
  all: GridHeaders,
  gridKey: string,
  field: string,
  originalName: string,
  next: string,
): GridHeaders {
  const value = next.trim();
  const grid = { ...(all[gridKey] ?? {}) };

  if (value === '' || value === originalName) delete grid[field];
  else grid[field] = value;

  const result = { ...all };
  if (Object.keys(grid).length === 0) delete result[gridKey];
  else result[gridKey] = grid;
  return result;
}

/** Move a header band as one block, retaining its displayed child order. */
export function moveColumnBand(order: readonly number[], band: readonly number[], target: number, after = false): number[] {
  if (band.includes(target) || !order.includes(target)) return [...order];
  const moving = order.filter((index) => band.includes(index));
  const remaining = order.filter((index) => !band.includes(index));
  remaining.splice(remaining.indexOf(target) + (after ? 1 : 0), 0, ...moving);
  return remaining;
}
